"use client";

import * as React from "react";
import { createPortal } from "react-dom";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import {
  ChevronLeft,
  ChevronRight,
  Crown,
  Flame,
  Heart,
  LayoutGrid,
  Library,
  ListMusic,
  LogIn,
  LogOut,
  Search,
  Sparkles,
  User,
  X,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  GeramaLogo,
  Sheet,
  SheetContent,
  SheetTitle,
  Skeleton,
  ThemeToggle,
} from "@gerama/ui";
import type { SiteUserPlanSummary } from "@gerama/types";
import { useSiteAuthStore } from "@gerama/stores";
import { cn } from "@gerama/utils";
import { NotificationBell } from "@/features/notifications/notification-bell";
import { BrowseCategoriesPopover } from "@/features/discover/browse-categories-popover";
import { isActivePath, DISCOVER_ITEMS, MORE_ITEMS } from "./nav-items";
import { BRAND } from "@/lib/brand";
import { openLogin } from "@/features/auth/login-modal";
import { useMyProfile } from "@/features/account/use-profile";
import { UserAvatar, userSubtitle } from "@/features/account/user-avatar";

/**
 * Sticky bar at the top of the main panel: history arrows and a search
 * field on desktop (Spotify's top bar). On a phone it is an app header:
 * logo, notifications and a "more" button that opens a bottom sheet (home,
 * search, browse and the library are tabs in `MobileNav`). Turns frosted
 * once the page scrolls under it.
 */
export function TopBar() {
  const router = useRouter();
  const pathname = usePathname();
  const [scrolled, setScrolled] = React.useState(false);
  const [menuOpen, setMenuOpen] = React.useState(false);

  React.useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  React.useEffect(() => setMenuOpen(false), [pathname]);

  return (
    <header
      className={cn(
        "sticky top-0 z-30 transition-colors md:rounded-t-xl",
        scrolled
          ? "bg-background/75 max-md:border-border/50 backdrop-blur-xl backdrop-saturate-150 max-md:border-b"
          : "bg-transparent",
      )}
      style={{ paddingTop: "env(safe-area-inset-top)" }}
    >
      <div className="flex h-14 items-center gap-2 px-4 md:h-16">
        <Link href="/" aria-label={BRAND} className="flex items-center md:hidden">
          <GeramaLogo className="h-7" title="" />
        </Link>

        <div className="hidden items-center gap-2 md:flex">
          {/* RTL: "back" points right. */}
          <HistoryButton label="بازگشت" onClick={() => router.back()}>
            <ChevronRight className="size-5" />
          </HistoryButton>
          <HistoryButton label="جلو" onClick={() => router.forward()}>
            <ChevronLeft className="size-5" />
          </HistoryButton>
        </div>

        <TopSearch />
        <Link
          href="/trending"
          className="solid-primary text-primary-foreground hidden items-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-bold transition-transform hover:scale-[1.03] lg:inline-flex"
        >
          <Flame className="size-4" />
          پرطرفدار
        </Link>

        <div className="ms-auto flex items-center gap-1">
          <NotificationBell />
          <div className="hidden md:block">
            <ThemeToggle />
          </div>
          <AccountMenu />
          <button
            type="button"
            onClick={() => setMenuOpen(true)}
            aria-label="بیشتر"
            aria-expanded={menuOpen}
            className="text-foreground/90 p-2 active:scale-90 md:hidden"
          >
            <LayoutGrid className="size-5" />
          </button>
        </div>
      </div>

      {menuOpen && <MoreSheet pathname={pathname} onClose={() => setMenuOpen(false)} />}
    </header>
  );
}

/** Phone "more" menu: a bottom sheet of tiles, like a native app's overflow screen. */
function MoreSheet({ pathname, onClose }: { pathname: string; onClose: () => void }) {
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    const { overflow } = document.body.style;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = overflow;
    };
  }, [onClose]);

  // Portalled: the frosted header's backdrop-filter would otherwise trap a fixed child inside it.
  return createPortal(
    <div
      className="fixed inset-0 z-[60] md:hidden"
      role="dialog"
      aria-modal="true"
      aria-label="بیشتر"
    >
      <button
        type="button"
        aria-label="بستن"
        onClick={onClose}
        className="animate-in fade-in absolute inset-0 bg-black/60 duration-200"
      />
      <div
        className="bg-card animate-in slide-in-from-bottom absolute inset-x-0 bottom-0 rounded-t-3xl px-4 pt-2 shadow-2xl duration-300"
        style={{ paddingBottom: "calc(1rem + env(safe-area-inset-bottom))" }}
      >
        <div className="bg-foreground/20 mx-auto mb-3 h-1 w-10 rounded-full" aria-hidden />
        <div className="mb-3 flex items-center justify-between">
          <p className="text-base font-bold">بیشتر</p>
          <button
            type="button"
            onClick={onClose}
            aria-label="بستن"
            className="bg-foreground/10 flex size-8 items-center justify-center rounded-full"
          >
            <X className="size-4" />
          </button>
        </div>
        <SheetProfileCard onClose={onClose} />
        <nav className="grid grid-cols-3 gap-2">
          {[...DISCOVER_ITEMS, ...MORE_ITEMS].map((item) => {
            const Icon = item.icon;
            const active = isActivePath(pathname, item.href);
            return (
              <Link
                key={item.href}
                href={item.href}
                onClick={onClose}
                className={cn(
                  "bg-foreground/5 flex flex-col items-center gap-2 rounded-2xl px-2 py-4 text-sm font-semibold transition-transform active:scale-95",
                  active ? "text-primary" : "text-foreground/90",
                )}
              >
                <Icon className="size-6" />
                {item.title}
              </Link>
            );
          })}
        </nav>
        <div className="bg-foreground/5 mt-3 flex items-center justify-between rounded-2xl ps-4 pe-1">
          <span className="text-sm font-semibold">حالت روشن / تاریک</span>
          <ThemeToggle />
        </div>
      </div>
    </div>,
    document.body,
  );
}

function HistoryButton({
  label,
  onClick,
  children,
}: {
  label: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className="bg-background/70 text-foreground hover:bg-background flex size-8 items-center justify-center rounded-full"
    >
      {children}
    </button>
  );
}

/** Desktop search pill — submits to `/search?q=` (the search page does the rest). */
function TopSearch() {
  const router = useRouter();
  const [value, setValue] = React.useState("");
  return (
    <form
      role="search"
      className="hidden w-full max-w-md md:block"
      onSubmit={(e) => {
        e.preventDefault();
        const q = value.trim();
        router.push(q ? `/search?q=${encodeURIComponent(q)}` : "/search");
      }}
    >
      <div className="bg-muted/80 focus-within:ring-foreground/60 hover:bg-muted flex h-11 items-center gap-2 rounded-full px-4 focus-within:ring-2">
        <Search className="text-muted-foreground size-5 shrink-0" />
        <input
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="آهنگ، خواننده یا پادکست…"
          aria-label="جستجو"
          className="placeholder:text-muted-foreground min-w-0 flex-1 bg-transparent text-sm outline-none"
        />
        <BrowseCategoriesPopover />
      </div>
    </form>
  );
}

/** Jalali date without the time — "۲۵ مهر ۱۴۰۵". */
function formatPlanDate(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("fa-IR", { dateStyle: "long" }).format(date);
}

/**
 * The plan line under the name: the active plan with its end date, or a nudge
 * to subscribe. A skeleton while `GET /users/me` is still loading.
 */
function PlanLine({
  plan,
  loading,
  className,
}: {
  plan: SiteUserPlanSummary | null | undefined;
  loading: boolean;
  className?: string;
}) {
  if (loading && plan === undefined)
    return <Skeleton className={cn("h-5 w-28 rounded-full", className)} />;
  if (!plan) {
    return (
      <span
        className={cn(
          "bg-foreground/10 text-muted-foreground inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium",
          className,
        )}
      >
        حساب رایگان
      </span>
    );
  }
  return (
    <span
      className={cn(
        "bg-primary/15 text-primary inline-flex max-w-full items-center gap-1 truncate rounded-full px-2 py-0.5 text-xs font-semibold",
        className,
      )}
    >
      <Crown className="size-3.5 shrink-0" />
      <span className="truncate">
        {plan.isTrial ? "آزمایشی" : "اشتراک"} {plan.name}
        {plan.source === "family" ? " (خانواده)" : ""} · تا {formatPlanDate(plan.expiresAt)}
      </span>
    </span>
  );
}

/** Top of the phone "more" sheet: who is signed in (tap → account), or a sign-in call to action. */
function SheetProfileCard({ onClose }: { onClose: () => void }) {
  const user = useSiteAuthStore((s) => s.user);
  const profile = useMyProfile();

  if (!user) {
    return (
      <button
        type="button"
        onClick={() => {
          onClose();
          openLogin();
        }}
        className="bg-primary/10 mb-3 flex w-full items-center gap-3 rounded-2xl p-3 text-start transition-transform active:scale-[0.98]"
      >
        <span className="solid-primary text-primary-foreground flex size-12 shrink-0 items-center justify-center rounded-full">
          <User className="size-6" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-bold">ورود یا ثبت‌نام</span>
          <span className="text-muted-foreground block text-xs">
            پسندیده‌ها، ذخیره‌شده‌ها و پلی‌لیست‌هایت همه‌جا با تو
          </span>
        </span>
        <ChevronLeft className="text-muted-foreground size-5 shrink-0" />
      </button>
    );
  }

  const subtitle = userSubtitle(user);
  return (
    <Link
      href="/account"
      onClick={onClose}
      className="bg-foreground/5 mb-3 flex items-center gap-3 rounded-2xl p-3 transition-transform active:scale-[0.98]"
    >
      <UserAvatar user={user} large className="size-14" fallbackClassName="text-xl" />
      <span className="min-w-0 flex-1 space-y-1">
        <span className="block truncate text-base font-bold">{user.name || "حساب من"}</span>
        {subtitle && (
          <span className="text-muted-foreground block truncate text-xs" dir="ltr">
            {subtitle}
          </span>
        )}
        <PlanLine plan={profile.data?.plan} loading={profile.isPending} />
      </span>
      <ChevronLeft className="text-muted-foreground size-5 shrink-0" />
    </Link>
  );
}

/** Phones get the account menu as a bottom sheet; a dropdown from the avatar is cramped there. */
function useIsPhone() {
  const [phone, setPhone] = React.useState(false);
  React.useEffect(() => {
    const query = window.matchMedia("(max-width: 767px)");
    const update = () => setPhone(query.matches);
    update();
    query.addEventListener("change", update);
    return () => query.removeEventListener("change", update);
  }, []);
  return phone;
}

function AccountMenu() {
  const user = useSiteAuthStore((s) => s.user);
  const clearSession = useSiteAuthStore((s) => s.clearSession);
  const router = useRouter();
  const phone = useIsPhone();
  const [sheetOpen, setSheetOpen] = React.useState(false);
  // Refreshes the stored profile (name, avatar) and carries the plan badge.
  const profile = useMyProfile();

  if (!user) {
    return (
      <button
        type="button"
        onClick={openLogin}
        className="text-muted-foreground hover:text-foreground hidden items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-semibold md:inline-flex"
      >
        <LogIn className="size-4" />
        ورود
      </button>
    );
  }

  const subtitle = userSubtitle(user);
  const plan = profile.data?.plan;
  const links = [
    { href: "/account", icon: User, label: "حساب کاربری" },
    { href: "/library/liked", icon: Heart, label: "پسندیده‌ها" },
    { href: "/library/playlists", icon: ListMusic, label: "پلی‌لیست‌های من" },
    { href: "/library/songs", icon: Library, label: "موسیقی من" },
    { href: "/plans", icon: Sparkles, label: plan ? "مدیریت اشتراک" : "خرید اشتراک ویژه" },
  ];
  const signOut = () => {
    clearSession();
    router.push("/");
  };

  const triggerClass =
    "bg-background/70 hover:bg-background focus-visible:ring-ring flex items-center gap-2 rounded-full p-1 outline-none focus-visible:ring-2 md:pe-3";
  const triggerContent = (
    <>
      <span className="relative">
        <UserAvatar user={user} className="size-7" />
        {plan && (
          <span
            className="bg-primary text-primary-foreground ring-background absolute -end-1 -bottom-1 flex size-3.5 items-center justify-center rounded-full ring-2"
            aria-hidden
          >
            <Crown className="size-2.5" />
          </span>
        )}
      </span>
      <span className="hidden max-w-28 truncate text-sm font-semibold md:inline">
        {user.name || "حساب من"}
      </span>
    </>
  );
  const profileCard = (
    <div className="from-primary/20 flex items-center gap-3 rounded-t-md bg-gradient-to-b to-transparent p-4">
      <UserAvatar user={user} large className="size-14" fallbackClassName="text-xl" />
      <div className="min-w-0 flex-1 space-y-1">
        <p className="truncate text-base font-bold">{user.name || "حساب من"}</p>
        {subtitle && (
          <p className="text-muted-foreground truncate text-xs" dir="ltr">
            {subtitle}
          </p>
        )}
        <PlanLine plan={plan} loading={profile.isPending} />
      </div>
    </div>
  );

  if (phone) {
    const row =
      "hover:bg-accent active:bg-accent flex w-full items-center gap-3 rounded-lg px-3 py-3 text-start text-sm font-medium";
    return (
      <>
        <button
          type="button"
          aria-label="حساب کاربری"
          onClick={() => setSheetOpen(true)}
          className={triggerClass}
        >
          {triggerContent}
        </button>
        <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
          <SheetContent dir="rtl">
            <SheetTitle className="sr-only">حساب کاربری</SheetTitle>
            {profileCard}
            <div className="overflow-y-auto overscroll-contain border-t p-2">
              {links.map(({ href, icon: Icon, label }) => (
                <Link key={href} href={href} onClick={() => setSheetOpen(false)} className={row}>
                  <Icon className="size-5" />
                  {label}
                </Link>
              ))}
            </div>
            <div className="border-t p-2">
              <button
                type="button"
                onClick={() => {
                  setSheetOpen(false);
                  signOut();
                }}
                className={cn(row, "text-destructive")}
              >
                <LogOut className="size-5" />
                خروج از حساب
              </button>
            </div>
          </SheetContent>
        </Sheet>
      </>
    );
  }

  return (
    <DropdownMenu dir="rtl">
      <DropdownMenuTrigger aria-label="حساب کاربری" className={triggerClass}>
        {triggerContent}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72 p-0">
        <DropdownMenuLabel className="p-0 font-normal">{profileCard}</DropdownMenuLabel>
        <DropdownMenuSeparator className="my-0" />
        <div className="p-1">
          {links.map(({ href, icon: Icon, label }) => (
            <DropdownMenuItem key={href} onSelect={() => router.push(href)}>
              <Icon className="size-4" />
              {label}
            </DropdownMenuItem>
          ))}
        </div>
        <DropdownMenuSeparator className="my-0" />
        <div className="p-1">
          <DropdownMenuItem variant="destructive" onSelect={signOut}>
            <LogOut className="size-4" />
            خروج از حساب
          </DropdownMenuItem>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
